/**
 * BizOne offline database (IndexedDB via Dexie). v2 schema with Phase 5
 * additions: inventory_states, outbox events, pin_verifiers, entitlement cache.
 *
 * Partitioning: every row carries `workspace_id` so switching workspaces
 * never leaks data across businesses.
 */
import Dexie from "dexie";

export const db = new Dexie("bizone-offline");

db.version(1).stores({
  products: "product_id, workspace_id, sku, barcode, name",
  categories: "category_id, workspace_id",
  customers: "customer_id, workspace_id, email, phone",
  orders: "local_id, workspace_id, status, created_at",
  mutations: "++id, workspace_id, entity, method, created_at, tries, status",
  meta: "key",
});

// v2 — Phase 5 additions. Dexie upgrades IN PLACE — pending outbox entries
// in `mutations` are preserved.
db.version(2).stores({
  products: "product_id, workspace_id, sku, barcode, name",
  categories: "category_id, workspace_id",
  customers: "customer_id, workspace_id, email, phone",
  orders: "local_id, workspace_id, status, created_at",
  mutations: "++id, workspace_id, entity, method, created_at, tries, status",
  meta: "key",
  // NEW
  inventory_states: "[workspace_id+inventory_item_id], workspace_id, inventory_item_id",
  outbox: "event_id, workspace_id, device_id, status, event_type, created_at_device",
  pin_verifiers: "[workspace_id+staff_id], workspace_id, staff_id, username",
  entitlements: "workspace_id",
  held_carts: "held_id, workspace_id, device_id, created_at",
});

export async function setMeta(key, value) {
  await db.meta.put({ key, value, updated_at: new Date().toISOString() });
}

export async function getMeta(key, fallback = null) {
  const row = await db.meta.get(key);
  return row ? row.value : fallback;
}

/** Spec §C — hard partitioning on logout / workspace switch. */
export async function clearWorkspaceCache(workspace_id) {
  await Promise.all([
    db.products.where({ workspace_id }).delete(),
    db.categories.where({ workspace_id }).delete(),
    db.customers.where({ workspace_id }).delete(),
    db.orders.where({ workspace_id }).delete(),
    db.inventory_states.where({ workspace_id }).delete(),
    db.pin_verifiers.where({ workspace_id }).delete(),
    db.entitlements.where({ workspace_id }).delete(),
    db.held_carts.where({ workspace_id }).delete(),
    // DO NOT clear `mutations` for this workspace if any are still PENDING_SYNC —
    // the caller must handle that via warnPendingBeforeLogout().
  ]);
}

/** Spec §V — refuse to clear if unsynced mutations exist. */
export async function pendingSyncCount(workspace_id) {
  return db.outbox.where({ workspace_id }).and((x) => x.status === "PENDING_SYNC" || x.status === "SYNC_ERROR").count();
}

export default db;
